import { useState } from 'react';
import type { FormEvent } from 'react';

import { PageHeader } from '@/components/PageHeader';
import { QueryState } from '@/components/QueryState';
import { shortDate, today } from '@/lib/format';
import { useConfirmEquipmentLog, useCreateEquipmentLog, useEquipmentLogs } from '@/lib/queries';
import { useSite } from '@/lib/useSite';

/**
 * 장비 가동일보.
 *
 * <p>가동시간이 <b>대여료와 유류비의 근거</b>다. 지금 이 값은 종이로 돌고 월말에 청구서와 맞춰
 * 보는데, 그때는 이미 어느 날 몇 시간이었는지 아무도 기억하지 못한다.
 *
 * <p>협력사가 적고 감리가 확인한다 — 적은 쪽이 스스로 확인하지 않는다.
 */
export function EquipmentLogsPage() {
  const { siteId, siteName, isJudge, isVendor } = useSite();
  const logs = useEquipmentLogs(siteId);
  const create = useCreateEquipmentLog(siteId);
  const confirm = useConfirmEquipmentLog(siteId);

  const [open, setOpen] = useState(false);
  const [logDate, setLogDate] = useState(today);
  const [equipmentName, setEquipmentName] = useState('');
  const [spec, setSpec] = useState('');
  const [operatingHours, setOperatingHours] = useState('');
  const [fuelLiters, setFuelLiters] = useState('');
  const [operatorName, setOperatorName] = useState('');
  const [workContent, setWorkContent] = useState('');

  const rows = logs.data ?? [];
  // 적는 것은 협력사만, 확인은 판정하는 손(감리·직원)이다(DOC-87 §2-3).
  const canWrite = isVendor;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    await create.mutateAsync({
      logDate,
      equipmentName: equipmentName.trim(),
      spec: spec.trim() === '' ? null : spec.trim(),
      operatingHours: Number(operatingHours.replace(/[^0-9.]/g, '')),
      fuelLiters: fuelLiters.trim() === '' ? null : Number(fuelLiters.replace(/[^0-9.]/g, '')),
      operatorName: operatorName.trim() === '' ? null : operatorName.trim(),
      workContent: workContent.trim() === '' ? null : workContent.trim(),
    });
    setOpen(false);
    setEquipmentName('');
    setSpec('');
    setOperatingHours('');
    setFuelLiters('');
    setWorkContent('');
  }

  return (
    <>
      <PageHeader
        title="장비 가동"
        subtitle={siteName}
        action={
          canWrite && (
            <button type="button" className="btn-primary px-3 py-1.5 text-ui-xs" onClick={() => setOpen(true)}>
              등록
            </button>
          )
        }
      />

      {open && (
        <form onSubmit={onSubmit} className="card mb-3">
          <div className="mb-3 flex gap-2">
            <label className="flex-1">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>가동일</span>
              <input type="date" className="input-field" value={logDate} onChange={(e) => setLogDate(e.target.value)} required />
            </label>
            <label className="flex-1">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>운전원</span>
              <input type="text" className="input-field" value={operatorName} onChange={(e) => setOperatorName(e.target.value)} />
            </label>
          </div>
          <div className="mb-3 flex gap-2">
            <label className="flex-1">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>장비명</span>
              <input type="text" className="input-field" value={equipmentName} onChange={(e) => setEquipmentName(e.target.value)} placeholder="굴착기" required />
            </label>
            <label className="flex-1">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>규격</span>
              <input type="text" className="input-field" value={spec} onChange={(e) => setSpec(e.target.value)} placeholder="0.7㎥" />
            </label>
          </div>
          <div className="mb-3 flex gap-2">
            <label className="flex-1">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>가동시간</span>
              <input
                type="text"
                inputMode="decimal"
                className="input-field"
                value={operatingHours}
                onChange={(e) => setOperatingHours(e.target.value)}
                placeholder="8"
                required
              />
            </label>
            <label className="flex-1">
              <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>유류(ℓ)</span>
              <input
                type="text"
                inputMode="decimal"
                className="input-field"
                value={fuelLiters}
                onChange={(e) => setFuelLiters(e.target.value)}
              />
            </label>
          </div>
          <label className="mb-4 block">
            <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>작업내용</span>
            <textarea className="input-field" rows={2} value={workContent} onChange={(e) => setWorkContent(e.target.value)} />
          </label>
          <div className="flex gap-2">
            <button type="submit" className="btn-primary flex-1" disabled={create.isPending}>
              {create.isPending ? '보내는 중입니다…' : '등록'}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setOpen(false)}>취소</button>
          </div>
        </form>
      )}

      <QueryState
        pending={logs.isPending}
        error={logs.isError}
        empty={rows.length === 0}
        emptyText="등록된 가동일보가 없습니다."
      >
        <ul className="flex flex-col gap-2">
          {rows.map((row) => (
            <li key={row.id} className="card">
              <div className="mb-1 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate text-ui-md font-medium">
                    {row.equipmentName}
                    {row.spec !== null && (
                      <span className="ml-1 text-ui-2xs" style={{ color: 'var(--text-muted)' }}>{row.spec}</span>
                    )}
                  </div>
                  <div className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
                    {shortDate(row.logDate)}
                    {row.operatorName !== null && ` · ${row.operatorName}`}
                    {row.fuelLiters !== null && ` · 유류 ${row.fuelLiters.toLocaleString('ko-KR')}ℓ`}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="text-ui-lg font-semibold">{row.operatingHours}시간</span>
                  {row.confirmedOn !== null && <span className="badge badge-gray">확인</span>}
                </div>
              </div>

              {row.workContent !== null && <p className="text-ui-sm">{row.workContent}</p>}

              {isJudge && row.confirmedOn === null && (
                <button
                  type="button"
                  className="btn-primary mt-3 w-full"
                  onClick={() => confirm.mutate(row.id)}
                  disabled={confirm.isPending}
                >
                  확인
                </button>
              )}

              {row.confirmedOn !== null && (
                <div className="mt-2 text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
                  {shortDate(row.confirmedOn)} {row.confirmedBy ?? ''} 확인
                </div>
              )}
            </li>
          ))}
        </ul>
      </QueryState>
    </>
  );
}
